// apps/workflow/src/pages/client/Payment.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  CreditCard,
  DollarSign,
  Calendar,
  Clock,
  Plus,
  RefreshCw,
  Download,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helper: status badge ──────────────────────────────────────
const getStatusBadge = (status) => {
  const config = {
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800' },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
    refunded: { label: 'Refunded', color: 'bg-slate-100 text-slate-800' },
  };
  return config[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-800' };
};

// ─── Main Component ──────────────────────────────────────────────
const ClientPayment = () => {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    method: 'bank_transfer',
    date: new Date().toISOString().split('T')[0],
  });
  const [submitting, setSubmitting] = useState(false);

  // ─── Fetch payments ──────────────────────────────────────────
  const fetchPayments = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const records = await pb.collection('payments').getFullList({
        filter: `client = "${user.id}"`,
        sort: '-created',
        requestKey: null,
      });
      setPayments(records);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Fetch payments error:', error);
        toast.error('Failed to load payment history');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPayments();
    setRefreshing(false);
    toast.success('Payment history refreshed');
  };

  // ─── Stats ──────────────────────────────────────────────────
  const totalPaid = payments
    .filter((p) => p.status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const pendingCount = payments.filter((p) => p.status === 'pending').length;

  const lastPaymentDate = payments.length > 0
    ? new Date(payments[0].created).toLocaleDateString()
    : 'N/A';

  // ─── Handle payment submission (mock) ──────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const newPayment = {
        client: user.id,
        amount: parseFloat(formData.amount),
        method: formData.method,
        paymentDate: formData.date,
        status: 'completed',
        reference: `PAY-${Date.now()}`,
      };
      await pb.collection('payments').create(newPayment);
      toast.success(`Payment of ₹${formData.amount} recorded successfully`);
      setShowModal(false);
      setFormData({ amount: '', method: 'bank_transfer', date: new Date().toISOString().split('T')[0] });
      fetchPayments();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px] space-y-4">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading payment history...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <CreditCard className="w-6 h-6 text-indigo-600" />
            Payments
          </h1>
          <p className="text-sm text-slate-500">Manage your payment history and make new payments</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 shadow-sm"
          >
            <Plus className="w-4 h-4" /> New Payment
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Total Paid"
          value={`₹${totalPaid.toLocaleString()}`}
          icon={DollarSign}
          color="emerald"
        />
        <StatCard
          title="Pending Payments"
          value={pendingCount}
          icon={Clock}
          color="amber"
        />
        <StatCard
          title="Last Payment"
          value={lastPaymentDate}
          icon={Calendar}
          color="indigo"
        />
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50">
          <h2 className="text-lg font-semibold text-slate-800">Payment History</h2>
        </div>
        <div className="overflow-x-auto">
          {payments.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <CreditCard className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No payments recorded yet.</p>
              <button
                onClick={() => setShowModal(true)}
                className="mt-3 text-indigo-600 hover:text-indigo-800 font-medium text-sm"
              >
                Make your first payment
              </button>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Date</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Method</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Reference</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payments.map((payment) => {
                  const status = getStatusBadge(payment.status);
                  return (
                    <tr key={payment.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-6 py-3 whitespace-nowrap">
                        {new Date(payment.created).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-3 font-medium text-slate-800">
                        ₹{payment.amount?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-3 capitalize">{payment.method || '—'}</td>
                      <td className="px-6 py-3">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-slate-500">{payment.reference || '—'}</td>
                      <td className="px-6 py-3 text-right">
                        <button
                          onClick={() => toast.info('Receipt download coming soon')}
                          className="text-indigo-600 hover:text-indigo-800 transition"
                          title="Download Receipt"
                        >
                          <Download className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-slate-800">Make a Payment</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                  required
                  placeholder="Enter amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Method</label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="card">Credit/Debit Card</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? 'Processing...' : 'Pay Now'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── StatCard Subcomponent ──────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

export default ClientPayment;